/**
 * Family Dashboard Page
 *
 * Main landing page for family members with real-time data
 */

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Calendar,
  MessageCircle,
  ClipboardList,
  Heart,
  ArrowRight,
  CheckCircle,
  FileText,
  ShieldCheck,
  Sparkles,
  Send,
} from 'lucide-react';
import { Card } from '@/core/components';
import { useAcknowledgeFamilyItem, useFamilyAcknowledgements, useFamilyDashboard } from '../hooks';
import { useAuth } from '@/core/hooks';
import { UpcomingVisits, ActivityFeed, VisitNotifications } from '../components';
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';
import type { UUID } from '@fuchsiahomecarecatalin/core/browser';

const FAMILY_ACK_KEY = 'family:acknowledgements';
const FAMILY_MESSAGE_DRAFT_KEY = 'family:message-draft';

const permittedDocuments = [
  {
    id: 'care-plan-summary',
    title: 'Care plan summary',
    description: 'Family-safe goals, routines and agreed care focus.',
  },
  {
    id: 'visit-summary',
    title: 'Visit summaries',
    description: 'Completed visit updates and non-internal care notes.',
  },
  {
    id: 'communication-policy',
    title: 'Communication policy',
    description: 'How the family can contact the office and care team.',
  },
];

const standardFamilyMessages = [
  {
    subject: 'Question about today visit',
    message: 'Hello, could you please confirm how today visit went and whether there is anything the family should know?',
  },
  {
    subject: 'Request weekly summary',
    message: 'Hello, could you please send a short weekly summary of visits and any family-safe updates?',
  },
  {
    subject: 'Update contact preferences',
    message: 'Hello, I would like to update how I receive family portal notifications.',
  },
];

function getAcknowledgements(): Record<string, string> {
  return getLocalStorage<Record<string, string>>(FAMILY_ACK_KEY, {});
}

function saveAcknowledgement(id: string) {
  const next = { ...getAcknowledgements(), [id]: new Date().toISOString() };
  setLocalStorage(FAMILY_ACK_KEY, next);
  return next;
}

function buildFamilySafeSummary(dashboardData: any, clientName: string): string {
  const visits = dashboardData?.upcomingVisits ?? [];
  const activity = dashboardData?.recentActivity ?? [];
  const completedVisits = visits.filter((visit: any) => visit.status === 'COMPLETED');
  const nextVisit = visits.find((visit: any) => visit.status !== 'COMPLETED');

  return [
    `I understand that you want a family-safe summary for ${clientName}.`,
    '',
    'Recommended next steps:',
    '1. Check the latest visit update first.',
    '2. Use Messages only if something is unclear, so the office receives one clear question.',
    '3. Review permitted documents before calling the office for routine information.',
    '',
    `Result: ${completedVisits.length} completed visit update${completedVisits.length === 1 ? '' : 's'} available, ${activity.length} recent activity update${activity.length === 1 ? '' : 's'} visible to family${nextVisit ? `, next visit is ${nextVisit.scheduledStartTime || nextVisit.scheduledStartTimeFormatted || 'scheduled soon'}` : ''}. Internal staff notes are not shown.`,
  ].join('\n');
}

function answerFamilyQuestionLocally(question: string, dashboardData: any, clientName: string): string {
  const normalized = question.toLowerCase();
  const visits = dashboardData?.upcomingVisits ?? [];
  const completedVisits = visits.filter((visit: any) => visit.status === 'COMPLETED');
  const nextVisit = visits.find((visit: any) => visit.status !== 'COMPLETED');
  const activities = dashboardData?.recentActivity ?? [];

  if (normalized.includes('visit') || normalized.includes('vizit')) {
    return [
      `I understand that you want information about ${clientName}'s visits.`,
      '',
      'Recommended next steps:',
      '1. Review the visit history below.',
      '2. If a visit is missing, send one message to the care team with the date and time.',
      '',
      `Result: ${completedVisits.length} completed visit update${completedVisits.length === 1 ? '' : 's'} are visible. ${nextVisit ? `The next visible visit is with ${nextVisit.caregiverName || 'the care team'}.` : 'No upcoming visit is currently visible.'}`,
    ].join('\n');
  }

  if (normalized.includes('care plan') || normalized.includes('plan')) {
    return [
      `I understand that you want the permitted care plan information for ${clientName}.`,
      '',
      'Recommended next steps:',
      '1. Open Care Plan for family-safe goals and progress.',
      '2. Ask the office only if you need something not visible in the portal.',
      '',
      `Result: ${dashboardData?.activeCarePlan?.name || 'The active care plan'} is visible with family-safe progress information only.`,
    ].join('\n');
  }

  return [
    `I understand that you want a family-safe answer about ${clientName}.`,
    '',
    'Recommended next steps:',
    '1. Check recent activity and visit history first.',
    '2. Use a standard message if you still need clarification.',
    '',
    `Result: I can answer only from permitted family portal data. Visible activity updates: ${activities.length}. Internal staff notes, payroll, safeguarding investigation details and private clinical/admin notes are filtered out.`,
  ].join('\n');
}

interface QuickLinkCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  to: string;
  badge?: number;
}

const QuickLinkCard: React.FC<QuickLinkCardProps> = ({ title, description, icon, to, badge }) => {
  return (
    <Link to={to}>
      <Card
        padding="md"
        hover
        className="h-full cursor-pointer border-2 border-transparent hover:border-primary-300"
      >
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
            <div className="text-primary-600">
              {icon}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900">{title}</h3>
              {badge !== undefined && badge > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">
                  {badge}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          </div>
          <ArrowRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
        </div>
      </Card>
    </Link>
  );
};

export const FamilyDashboard: React.FC = () => {
  const { user } = useAuth();
  const familyMemberId = user?.id as UUID | null;
  const [summary, setSummary] = useState('');
  const [familyQuestion, setFamilyQuestion] = useState('');
  const [familyAnswer, setFamilyAnswer] = useState('');
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isAnsweringQuestion, setIsAnsweringQuestion] = useState(false);
  const [acknowledgements, setAcknowledgements] = useState<Record<string, string>>(() => getAcknowledgements());

  const { data: dashboardData, isLoading, error } = useFamilyDashboard(familyMemberId);
  const { data: serverAcknowledgements } = useFamilyAcknowledgements(familyMemberId);
  const acknowledgeItem = useAcknowledgeFamilyItem(familyMemberId);

  // Use configured company data if no profile data is available.
  const clientName = dashboardData?.client?.name || 'Margaret Johnson';
  const firstName = clientName.split(' ')[0];
  const visibleVisits = dashboardData?.upcomingVisits || [];
  const completedVisits = visibleVisits.filter((visit: any) => visit.status === 'COMPLETED');
  const latestActivity = dashboardData?.recentActivity?.[0];
  const weeklySummary = useMemo(
    () => buildFamilySafeSummary(dashboardData, clientName),
    [dashboardData, clientName]
  );

  const acknowledge = (id: string, label: string) => {
    setAcknowledgements(saveAcknowledgement(id));
    if (familyMemberId) {
      acknowledgeItem.mutate({ itemId: id, label, itemType: id.startsWith('document-') ? 'document' : 'update' });
    } else {
      toast.success(`${label} confirmed as received`);
    }
  };

  const isAcknowledged = (id: string) =>
    Boolean(acknowledgements[id] || serverAcknowledgements?.some((item) => item.itemId === id));

  const prepareStandardMessage = (message: typeof standardFamilyMessages[number]) => {
    setLocalStorage(FAMILY_MESSAGE_DRAFT_KEY, message);
    toast.success('Message template prepared');
  };

  const generateFamilySummary = async () => {
    setIsGeneratingSummary(true);
    try {
      const response = await fetch('/api/ai/admin-assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: 'FAMILY',
          page: 'family-portal',
          question: 'Generate a family-safe daily and weekly care update. Use only permitted family portal data. Do not include internal notes, payroll, safeguarding investigation details or private admin information.',
          context: {
            client: dashboardData?.client,
            upcomingVisits: visibleVisits,
            recentActivity: dashboardData?.recentActivity,
            activeCarePlan: dashboardData?.activeCarePlan,
            allowedDataOnly: true,
          },
        }),
      });
      if (!response.ok) throw new Error(`AI returned ${response.status}`);
      const payload = (await response.json()) as { text?: string };
      setSummary(payload.text?.trim() || weeklySummary);
    } catch {
      setSummary(weeklySummary);
      toast.error('AI summary unavailable. Family-safe standard summary was created.');
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const answerFamilyQuestion = async () => {
    const question = familyQuestion.trim();
    if (!question) return;

    setIsAnsweringQuestion(true);
    try {
      const response = await fetch('/api/ai/admin-assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: 'FAMILY',
          page: 'family-portal-questions',
          question,
          context: {
            client: dashboardData?.client,
            upcomingVisits: visibleVisits,
            recentActivity: dashboardData?.recentActivity,
            activeCarePlan: dashboardData?.activeCarePlan,
            permittedDocuments,
            familySafetyRule: 'Answer only from permitted family-visible data. Do not expose internal staff notes, payroll, private safeguarding investigations or admin-only details.',
          },
        }),
      });
      if (!response.ok) throw new Error(`AI returned ${response.status}`);
      const payload = (await response.json()) as { text?: string };
      setFamilyAnswer(payload.text?.trim() || answerFamilyQuestionLocally(question, dashboardData, clientName));
    } catch {
      setFamilyAnswer(answerFamilyQuestionLocally(question, dashboardData, clientName));
      toast.error('AI answer unavailable. Family-safe local answer was created.');
    } finally {
      setIsAnsweringQuestion(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="h-20 bg-gray-200 rounded-lg" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64 bg-gray-200 rounded-lg" />
          <div className="h-64 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Welcome to Family Portal</h1>
          <p className="text-lg text-gray-600 mt-2">
            Here's what's happening with your loved one's care
          </p>
        </div>
        <Card padding="lg">
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Unable to Load Dashboard
            </h3>
            <p className="text-gray-600 mb-4">
              We're having trouble loading your dashboard data. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Refresh Page
            </button>
          </div>
        </Card>
      </div>
    );
  }

  const quickLinks = [
    {
      title: 'View Care Schedule',
      description: `See upcoming visits for ${firstName}`,
      icon: <Calendar className="h-6 w-6" />,
      to: '/family-portal/schedule',
    },
    {
      title: 'Messages',
      description: 'Communicate with care team',
      icon: <MessageCircle className="h-6 w-6" />,
      to: '/family-portal/messages',
      badge: dashboardData?.unreadMessages,
    },
    {
      title: 'Care Plan',
      description: 'View care goals and progress',
      icon: <ClipboardList className="h-6 w-6" />,
      to: '/family-portal/care-plan',
    },
    {
      title: 'Health Updates',
      description: 'Visit summaries and reports',
      icon: <Heart className="h-6 w-6" />,
      to: '/family-portal/health-updates',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Visit Notifications (renders nothing, just handles notifications) */}
      <VisitNotifications />

      {/* Welcome Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome, Family Member
        </h1>
        <p className="text-lg text-gray-600">
          Here's what's happening with {firstName}'s care
        </p>
      </div>

      {/* Quick Links */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Links</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quickLinks.map((link) => (
            <QuickLinkCard key={link.to} {...link} />
          ))}
        </div>
      </div>

      <section className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <Card padding="lg" className="border-2 border-emerald-100 bg-emerald-50">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-emerald-700">Automatic family update</p>
              <h2 className="mt-1 text-xl font-bold text-emerald-950">Latest permitted care update</h2>
              <p className="mt-2 text-sm leading-6 text-emerald-900">
                {latestActivity?.description || latestActivity?.summary || `No new visit update is waiting. When ${firstName}'s visit is completed, the family-safe update appears here.`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => acknowledge(`latest-update-${latestActivity?.id || 'none'}`, 'Latest family update')}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
            >
              <CheckCircle className="h-4 w-4" />
              {isAcknowledged(`latest-update-${latestActivity?.id || 'none'}`) ? 'Received' : 'Confirm received'}
            </button>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-white p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Completed visits</p>
              <p className="mt-2 text-2xl font-bold text-gray-950">{completedVisits.length}</p>
            </div>
            <div className="rounded-lg bg-white p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Unread messages</p>
              <p className="mt-2 text-2xl font-bold text-gray-950">{dashboardData?.unreadMessages ?? 0}</p>
            </div>
            <div className="rounded-lg bg-white p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Notifications</p>
              <p className="mt-2 text-2xl font-bold text-gray-950">{dashboardData?.unreadNotifications ?? 0}</p>
            </div>
          </div>
        </Card>

        <Card padding="lg">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-1 h-6 w-6 text-[#6c3a80]" />
            <div>
              <h2 className="text-xl font-bold text-gray-950">Family-safe information filter</h2>
              <p className="mt-2 text-sm leading-6 text-gray-600">
                The family portal shows visit updates, family-safe notes, care plan progress and permitted documents only. Internal staff notes, payroll, private admin records and restricted safeguarding details are filtered out.
              </p>
            </div>
          </div>
        </Card>
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <Card padding="lg">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-[#6c3a80]">Daily / weekly summary</p>
              <h2 className="mt-1 text-xl font-bold text-gray-950">Automatic summary for family</h2>
              <p className="mt-1 text-sm text-gray-600">Generate one clear update instead of calling the office for routine checks.</p>
            </div>
            <button
              type="button"
              onClick={() => void generateFamilySummary()}
              disabled={isGeneratingSummary}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5e3474] disabled:opacity-60"
            >
              <Sparkles className="h-4 w-4" />
              {isGeneratingSummary ? 'Generating...' : 'Generate summary'}
            </button>
          </div>
          <p className="mt-4 whitespace-pre-line rounded-lg bg-slate-50 p-4 text-sm leading-6 text-slate-700">
            {summary || weeklySummary}
          </p>
        </Card>

        <Card padding="lg">
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-[#6c3a80]">Family questions</p>
            <h2 className="mt-1 text-xl font-bold text-gray-950">Ask using permitted data only</h2>
            <p className="mt-1 text-sm text-gray-600">Answers are based only on family-visible visits, updates, care plan summary and permitted documents.</p>
          </div>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <input
              value={familyQuestion}
              onChange={(event) => setFamilyQuestion(event.target.value)}
              placeholder="Example: Was today's visit completed?"
              className="min-h-11 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
            />
            <button
              type="button"
              onClick={() => void answerFamilyQuestion()}
              disabled={isAnsweringQuestion}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5e3474] disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {isAnsweringQuestion ? 'Answering...' : 'Ask'}
            </button>
          </div>
          {familyAnswer && (
            <p className="mt-4 whitespace-pre-line rounded-lg bg-slate-50 p-4 text-sm leading-6 text-slate-700">
              {familyAnswer}
            </p>
          )}
        </Card>
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <Card padding="lg">
          <h2 className="text-xl font-bold text-gray-950">Standard messages to reduce office calls</h2>
          <div className="mt-4 space-y-3">
            {standardFamilyMessages.map((message) => (
              <div key={message.subject} className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-semibold text-gray-950">{message.subject}</p>
                  <p className="mt-1 text-sm text-gray-600">{message.message}</p>
                </div>
                <Link
                  to="/family-portal/messages"
                  onClick={() => prepareStandardMessage(message)}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                >
                  Use
                </Link>
              </div>
            ))}
          </div>
        </Card>

        <Card padding="lg">
          <h2 className="text-xl font-bold text-gray-950">Documents permitted for family</h2>
          <div className="mt-4 space-y-3">
            {permittedDocuments.map((document) => (
              <div key={document.id} className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <FileText className="mt-1 h-5 w-5 text-[#6c3a80]" />
                    <div>
                      <p className="font-semibold text-gray-950">{document.title}</p>
                      <p className="mt-1 text-sm text-gray-600">{document.description}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => acknowledge(`document-${document.id}`, document.title)}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    <CheckCircle className="h-4 w-4" />
                    {isAcknowledged(`document-${document.id}`) ? 'Received' : 'Confirm'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </section>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Visits */}
        <div>
          <UpcomingVisits visits={dashboardData?.upcomingVisits || []} />
        </div>

        {/* Recent Activity */}
        <div>
          <Card padding="md">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            </div>
            <ActivityFeed activities={dashboardData?.recentActivity || []} showLimit={5} />
          </Card>
        </div>
      </div>

      {/* Care Plan Summary */}
      {dashboardData?.activeCarePlan && (
        <Card padding="lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {dashboardData.activeCarePlan.name}
              </h2>
              <p className="text-sm text-gray-600 mt-1">Active Care Plan</p>
            </div>
            <Link
              to="/family-portal/care-plan"
              className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center gap-1"
            >
              View Details
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">
                {dashboardData.activeCarePlan.goalsTotal}
              </div>
              <div className="text-sm text-gray-600 mt-1">Total Goals</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-700">
                {dashboardData.activeCarePlan.goalsAchieved}
              </div>
              <div className="text-sm text-gray-600 mt-1">Goals Achieved</div>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Progress</span>
              <span className="font-medium">
                {dashboardData.activeCarePlan.goalsTotal > 0
                  ? Math.round(
                      (dashboardData.activeCarePlan.goalsAchieved /
                        dashboardData.activeCarePlan.goalsTotal) *
                        100
                    )
                  : 0}
                %
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all"
                style={{
                  width: `${
                    dashboardData.activeCarePlan.goalsTotal > 0
                      ? (dashboardData.activeCarePlan.goalsAchieved /
                          dashboardData.activeCarePlan.goalsTotal) *
                        100
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
